import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useToast } from "../../components/ui/Toast";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { useCreateAccessRequestMutation } from "../../api/libraryApi";
import { lockCardState, usageLine, withPendingRequest } from "./libraryRules";
import ConnectionState from "../../components/ui/ConnectionState";

export const LIBRARY_HOME = "/uygulama/kutuphane";
export const packagePath = (id) => `${LIBRARY_HOME}/${id}`;

export const inputCls = `w-full rounded-xl border border-terakkiBorder bg-terakkiSurface px-3 py-2 text-sm text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`;

/**
 * Kütüphane sayfalarının ortak kabı: ortada tek sütun panel, üstte açık
 * özet şeridi (geri bağlantısı, simge, başlık), altta isteğe bağlı sabit
 * düğme bölgesi. Kutusuz, çizgili satırlar (yumuşak tasarım).
 */
export function LibraryPanel({ Icon, title, subtitle, back, right, footer, wide = false, children }) {
  const { t } = useScriptMode();
  return (
    <div className={`w-full ${wide ? "" : "max-w-3xl mx-auto"} lg:h-full lg:min-h-0 flex flex-col`}>
      <div className={`${cls.panel} overflow-hidden flex flex-col lg:flex-1 lg:min-h-0`}>
        <div className={`shrink-0 flex items-center gap-3 px-4 py-3 ${cls.summary}`}>
          {back ? (
            <Link
              to={back}
              aria-label={t("lib_back")}
              title={t("lib_back")}
              className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.accent} ${cls.rowHover}`}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} aria-hidden />
            </Link>
          ) : Icon ? (
            <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
              <Icon sx={{ fontSize: 20 }} aria-hidden />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <h1 className={`text-lg font-Medium leading-tight truncate ${cls.heading}`}>{title}</h1>
            {subtitle ? <div className={`text-xs leading-tight mt-0.5 ${cls.muted}`}>{subtitle}</div> : null}
          </div>
          {right}
        </div>
        <div className="flex-1 min-h-0 lg:overflow-y-auto">{children}</div>
        {footer}
      </div>
    </div>
  );
}

/** Yumuşak sekme şeridi (sekme, sıralama, istek yönü). */
export function Segment({ value, options, onChange, label, small }) {
  const { t } = useScriptMode();
  return (
    <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={label}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => !active && onChange(o.value)}
            className={`${small ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"} rounded-full transition-colors ${
              active
                ? "bg-terakkiPrimaryDark text-white dark:bg-[#8FC9CA] dark:text-[#123B3D]"
                : `${cls.muted} ${cls.rowHover}`
            }`}
          >
            {t(o.key)}
          </button>
        );
      })}
    </div>
  );
}

/** Bölüm başlığı: "Sorular · 12" gibi ince yazı ve alt çizgi. */
export function SectionTitle({ children }) {
  return <h2 className={`px-4 pt-5 pb-2 text-xs uppercase tracking-wide ${cls.muted} ${cls.row}`}>{children}</h2>;
}

export function CenterMessage({ text, danger, onRetry, error, retrying }) {
  const { t } = useScriptMode();
  // Yükleme hatası (danger + onRetry): ortak ConnectionState.
  if (danger && onRetry) {
    return <ConnectionState error={error} onRetry={onRetry} retrying={retrying} message={text} />;
  }
  return (
    <div className="py-10 px-4 flex flex-col items-center gap-2 text-center">
      <p className={`text-sm ${danger ? cls.danger : cls.muted}`}>{text}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry} className={`text-sm ${cls.accent}`}>
          {t("lib_retry")}
        </button>
      ) : null}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="py-8 flex justify-center">
      <CircularProgress size={22} />
    </div>
  );
}

/**
 * Kilitli paket satırı: başlık, sahip, yıldız, kilit nedeni ve izin durumu.
 * Sorular gösterilmez; kilit ve istek durumu sunucudan geldiği gibidir.
 */
export function LockedPackageRow({ pkg, requesting, onRequest, to }) {
  const { t } = useScriptMode();
  const state = lockCardState(pkg);
  if (!state.locked) return null;
  const meta = [pkg.ownerName, `${pkg.starCount ?? 0} ${t("lib_word_star")}`].filter(Boolean).join(" · ");
  const usage = usageLine(pkg.usage, t);
  const pending = state.status === "pending";
  const info = (
    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
      <span className={`text-base truncate ${cls.title}`}>{pkg.title}</span>
      {meta ? <span className={`text-xs truncate ${cls.muted}`}>{meta}</span> : null}
      {usage ? <span className={`text-xs truncate ${cls.muted}`}>{usage}</span> : null}
      <span className={`text-xs ${cls.muted}`}>{t(state.reasonKey)}</span>
      {state.status === "rejected" ? (
        <span className={`text-xs ${cls.danger}`}>
          {state.reply ? `${t("lib_access_denied")} · ${state.reply}` : t("lib_access_denied")}
        </span>
      ) : null}
      {state.status === "stale" ? <span className={`text-xs ${cls.muted}`}>{t("lib_req_stale")}</span> : null}
    </div>
  );
  return (
    <li className={`flex items-center gap-3 px-4 py-3 ${cls.row}`}>
      <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cls.avatar}`}>
        <LockOutlinedIcon sx={{ fontSize: 18 }} aria-hidden />
      </span>
      {to ? (
        <Link to={to} className="min-w-0 flex-1 flex">
          {info}
        </Link>
      ) : (
        info
      )}
      <SoftButton
        tone={pending ? "soft" : "primary"}
        size="small"
        disabled={!state.canRequest || requesting}
        onClick={onRequest}
        sx={{ py: 0.4, px: 1.5, fontSize: 13, minWidth: 104 }}
      >
        {requesting ? <CircularProgress size={16} color="inherit" /> : t(state.buttonKey)}
      </SoftButton>
    </li>
  );
}

/**
 * İzin isteği gönderme: aynı anda tek istek; başarıda satır "İzin
 * bekleniyor" olur (onDone güncel paketi alır). Hata sunucudan gelir.
 */
export function useAccessRequester() {
  const { t } = useScriptMode();
  const toast = useToast();
  const [create] = useCreateAccessRequestMutation();
  const [requesting, setRequesting] = useState(null);
  const inFlight = useRef(false);

  const request = useCallback(
    async (pkg, onDone) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setRequesting(pkg.id);
      try {
        const result = await create({ packageId: pkg.id });
        if (result.error) {
          toast.error(result.error.message || t("mzq_request_failed"));
          return;
        }
        onDone?.(withPendingRequest(pkg, result.data?.data?.request?.id));
        toast.success(t("lib_access_requested"));
      } finally {
        inFlight.current = false;
        setRequesting(null);
      }
    },
    [create, t, toast]
  );

  return { requesting, request };
}

/** Evet/hayır onayı (sil, bildir, yayından kaldır, geri çek). */
export function ConfirmDialog({ open, title, text, confirmLabel, danger, busy, onConfirm, onClose }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, ...paperSx } }}>
      <DialogTitle sx={{ fontWeight: 500 }}>{title}</DialogTitle>
      {text ? (
        <DialogContent>
          <p className={`text-sm ${cls.muted}`}>{text}</p>
        </DialogContent>
      ) : null}
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={busy}>
          {t("muzakere_cancel")}
        </SoftButton>
        <SoftButton tone={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy}>
          {busy ? <CircularProgress size={18} color="inherit" /> : confirmLabel}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Kısa metinli onay penceresi: öneri mesajı ya da karar cevabı (isteğe
 * bağlı). Her açılışta boş başlar.
 */
export function MessageDialog({ open, title, hint, placeholder, sendLabel, danger, maxLength, busy, onSend, onClose }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [text, setText] = useState("");
  useEffect(() => {
    if (open) setText("");
  }, [open]);
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, ...paperSx } }}>
      <DialogTitle sx={{ fontWeight: 500 }}>{title}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-2 pt-1">
          {hint ? <p className={`text-sm ${cls.muted}`}>{hint}</p> : null}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={maxLength}
            rows={4}
            placeholder={placeholder}
            aria-label={placeholder}
            disabled={busy}
            className={inputCls}
          />
          <span className={`text-xs self-end ${cls.muted}`}>{`${text.length}/${maxLength}`}</span>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={busy}>
          {t("muzakere_cancel")}
        </SoftButton>
        <SoftButton tone={danger ? "danger" : "success"} onClick={() => onSend(text)} disabled={busy}>
          {busy ? <CircularProgress size={18} color="inherit" /> : sendLabel}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
